import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from "react-router-dom";
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useEntidad, useForm } from '../../components/hooks';
import {
    Loading,
    TableCustom,
    SectionHeading,
    AdvancedSearch,
    InputCuenta,
    InputPersona,
    InputEntidadMultiple,
    InputEjercicio,
    DatePickerCustom,
    InputWithEnterEvent,
    CheckboxButton,
    CheckboxButtonGroup
} from '../../components/common';
import CuentaCorrienteItemModal from '../../components/controls/CuentaCorrienteItemModal';
import { getDateToString, getFormatNumber, precisionRound } from '../../utils/convert';
import CuentaCorrienteCondicionesEspecialesGrid from '../../components/controls/CuentaCorrienteCondicionesEspecialesGrid';
import { useAccess } from '../../components/hooks/useAccess';
import { isNull } from '../../utils/validator';
import {usePermissions} from "../../components/hooks/usePermissions";
import {PERMISSIONS} from "../../consts/permissions";

function CuentaCorrienteView() {

    //parametros url
    const params = useParams();
    const [searchParams] = useSearchParams();
    const numeroPartidaInicial = searchParams.get('numeroPartida') ?? '';

    const entityInit = {
        cuenta: {
            id: 0,
            numeroCuenta: '',
            numeroWeb: '',
            idEstadoCuenta: 0,
            idTipoTributo: 0,
            idTributo: 0,
            fechaAlta: null,
            fechaBaja: null,
            idContribuyentePrincipal: 0,
            idDireccionPrincipal: 0,
            idDireccionEntrega: 0
        },
        contribuyente: {
            id: 0,
            idPersona: 0,
            idTipoPersona: 0,
            idTipoDocumento: 0,
            numeroDocumento: '',
            nombrePersona: '',
            fechaAlta: null
        }
    };

    //hooks
    const [state, setState] = useState({
        filter: params.filter,
        mode: params.mode,
        idCuenta: (params.filter === 'cuenta') ? parseInt(params.id) : 0,
        idContribuyente: (params.filter === 'contribuyente') ? parseInt(params.id) : 0,
        loading: false,
        showForm: false,
        rowId: 0,
        list: []
    });
    const [cuenta, setCuenta] = useState(entityInit.cuenta);
    const [contribuyente, setContribuyente] = useState(entityInit.contribuyente);
    const [items, setItems] = useState([]);
    const [dataCondicionEspecial, setDataCondicionEspecial] = useState({
        id: 0,
        idCuenta: 0,
        numeroPartida: 0,
        codigoDelegacion: '',
        numeroMovimiento: 0,
        showForm: false,
        modeEdit: false
    });

    const [filters, setFilters] = useState({
        labels: [
            { title: 'Tasa', field: 'descTasa', value: '', valueIgnore: '', fieldOriginal: 'idsTasa', valueIgnoreOriginal: []},
            { title: 'Sub tasa', field: 'descSubTasa', value: '', valueIgnore: '', fieldOriginal: 'idsSubTasa', valueIgnoreOriginal: []},
            { title: 'N° Partida', field: 'numeroPartida', value: numeroPartidaInicial, valueIgnore: ''},
            { title: 'Periodo/Cuota desde', field: 'descPeriodoCuotaDesde', value: '0000-00', valueIgnore: '0000-00'},
            { title: 'Periodo/Cuota hasta', field: 'descPeriodoCuotaHasta', value: '0000-00', valueIgnore: '0000-00'},
            { title: 'Fecha desde', field: 'descFechaMovimientoDesde', value: '', valueIgnore: ''},
            { title: 'Fecha hasta', field: 'descFechaMovimientoHasta', value: '', valueIgnore: ''},
            { title: 'Tipo movimiento', field: 'descTipoMovimiento', value: '', valueIgnore: '', fieldOriginal: 'idsTipoMovimiento', valueIgnoreOriginal: []},
            { title: 'N° Movimiento desde', field: 'numeroMovimientoDesde', value: '', valueIgnore: ''},
            { title: 'N° Movimiento hasta', field: 'numeroMovimientoHasta', value: '', valueIgnore: ''},
            { title: 'Considerar el saldo anterior', field: 'descSaldoAnterior', value: 'No', valueIgnore: ''},
            { title: 'Mostrar movimientos en cero', field: 'descMovimientoCero', value: 'No', valueIgnore: ''}
        ]
    });

    const mount = () => {

        if (params.filter === 'cuenta' && state.idCuenta > 0) {
            FindCuenta(state.idCuenta);
        }
        else if (params.filter === 'contribuyente' && state.idContribuyente > 0) {
            FindContribuyente(state.idContribuyente);
        }

        const unmount = () => {}
        return unmount;
    }
    useEffect(mount, []);

    const [formValues, formHandle, , formSet] = useForm({
        idsTasa: [],
        descTasa: '', //no existe como control, se usa para tomar el text de idsTasa
        idsSubTasa: [],
        descSubTasa: '',
        numeroPartida: numeroPartidaInicial,

        periodoDesde: '',
        cuotaDesde: '',
        descPeriodoCuotaDesde: '0000-00',
        periodoHasta: '',
        cuotaHasta: '',
        descPeriodoCuotaHasta: '0000-00',

        fechaMovimientoDesde: null,
        descFechaMovimientoDesde: '',
        fechaMovimientoHasta: null,
        descFechaMovimientoHasta: '',

        idsTipoMovimiento: [],
        descTipoMovimiento: '',

        numeroMovimientoDesde: '',
        numeroMovimientoHasta: '',
        saldoAnterior: false,
        descSaldoAnterior: 'No',
        movimientoCero: false,
        descMovimientoCero: 'No',
    });

    const [, getRowEntidad, entidadesReady] = useEntidad({
        entidades: ['Tasa','SubTasa','Rubro','TipoMovimiento'],
        onLoaded: (entidades, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        }
    });

    const { hasPermission } = usePermissions();
    const canEditCuentaCorriente = hasPermission(PERMISSIONS.CUENTA_CORRIENTE_EDIT);

    //definiciones
    const cellVMR = (props) =>  <div className='action'>
                                    <div onClick={ (event) => handleClickItemView(props.value) } className="link">
                                        <span className="material-symbols-outlined" title="Ver">search</span>
                                    </div>
                                    <div onClick={ (event) => handleClickItemViewCondicionEspecial(props.value) } className="link">
                                        <span className="material-symbols-outlined" title="Ver condiciones especiales">warning</span>
                                    </div>
                                </div>

    const getDescTipoMovimiento = (id) => {
        const row = getRowEntidad('TipoMovimiento', id);
        return (row) ? row.nombre : '';
    }
    const getCodigoTasa = (id) => {
        const row = getRowEntidad('Tasa', id);
        return (row) ? row.codigo : '';
    }
    const getDescTasa = (id) => {
        const row = getRowEntidad('Tasa', id);
        return (row) ? `${row.codigo} - ${row.descripcion}` : '';
    }
    const getDescSubTasa = (id) => {
        const row = getRowEntidad('SubTasa', id);
        return (row) ? `${row.codigo} - ${row.descripcion}` : '';
    }
    const getDescSeleccion = (ids, getDesc) =>
        (ids.length === 1) ? getDesc(ids[0]) :
        (ids.length > 1) ? `${ids.length} Seleccionados` : '';
    const getCodigoRubro = (id) => {
        const row = getRowEntidad('Rubro', id);
        return (row) ? row.codigo : '';
    }

    const GetFooterTotal = (info, columnName) => {
        const total = useMemo(
            () =>
            info.rows.reduce((sum, row) => row.values[columnName] + sum, 0),
            [info.rows]
        )

        return <>{getFormatNumber(total,2)}</>
    }

    const columnsTasa = [
        { Header: 'Código', accessor: 'codigo', width: '25%' },
        { Header: 'Descripción', accessor: 'descripcion', width: '70%' }
    ];

    const columnsSubTasa = [
        { Header: 'Tasa', Cell: (props) => getDescTasa(props.value), accessor: 'idTasa', width: '35%' },
        { Header: 'Código', accessor: 'codigo', width: '15%' },
        { Header: 'Descripción', accessor: 'descripcion', width: '50%' }
    ];

    const columnRubro = useMemo(() =>
        (cuenta.idTipoTributo === TRIBUTO_TYPE.COMERCIOS) ? [
            { Header: 'Rubro', accessor: 'codigoRubro', width: '7%', disableSortBy: true}
        ] : []
    , [cuenta.idTipoTributo])

    const tableColumns = useMemo(() => [
        // { Header: 'ID', accessor: 'operacion', width: '4%', alignCell: 'right', disableSortBy: true },
        { Header: 'Tasa', accessor: 'codigoTasa', width: '5%', disableSortBy: true, ellipsis: false},
        { Header: 'Sub tasa', accessor: 'descSubTasa', width: '17%', disableSortBy: true},
        { Header: 'Período', accessor: 'periodo', width: '6%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Cuota', accessor: 'cuota', width: '5%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        ...columnRubro,
        { Header: 'Fecha', Cell: (props) => (props.value) ? getDateToString(props.value, false) : '', accessor: 'fechaMovimiento', width: '7%', disableSortBy: true , ellipsis: false},
        { Header: 'Movimiento', Cell: (props) => getDescTipoMovimiento(props.value), accessor: 'idTipoMovimiento', width: '10%', disableSortBy: true},
        // { Header: 'N°', accessor: 'codigoMovimiento', alignCell: 'right', width: '8%', disableSortBy: true, ellipsis: false},
        { Header: 'Debe', Footer: (info) => GetFooterTotal(info ,'importeDebe'), Cell: (props) => getFormatNumber(props.value, 2), id: 'importeDebe', accessor: 'importeDebe', width: '7%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Haber', Footer: (info) => GetFooterTotal(info ,'importeHaber'), Cell: (props) => getFormatNumber(props.value, 2), id: 'importeHaber', accessor: 'importeHaber', width: '7%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Saldo', Cell: (props) => getFormatNumber(props.value, 2), accessor: 'importeSaldo', width: '7%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Vto.', Cell: (props) => getDateToString(props.value), accessor: 'fechaVencimiento2', width: '7%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: 'Partida', accessor: 'numeroPartida', width: '4%', alignCell: 'right', disableSortBy: true, ellipsis: false},
        { Header: '', Cell: cellVMR, id:'abm', accessor: 'id', width: '5%', disableGlobalFilter: true, disableSortBy: true, ellipsis: false}
    // columns estable salvo cambios reales: columnRubro (tributo), entidadesReady (refresca celdas vivas como
    // Movimiento al cargar entidades) e items (mantiene fresco el closure de cellVMR). Los helpers se omiten a
    // propósito: leen refs vivos (getRowEntidad) y se recapturan con estos deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [columnRubro, entidadesReady, items]);

    const activeColumns = useMemo(() =>
        (params.filter === 'cuenta') ? tableColumns :
        (params.filter === 'contribuyente') ? [
            { Header: 'Cuenta', accessor: 'idCuenta', width: '10%', disableSortBy: true, ellipsis: false},
            ...tableColumns
        ] : [],
    [params.filter, tableColumns]);

    const sortCuentaCorriente = (a,b) => (b.numeroPartida === a.numeroPartida) ? (b.cuota === a.cuota) ?  b.item - a.item : b.cuota - a.cuota : b.numeroPartida - a.numeroPartida;

    //handles
    const handleClickItemView = (id) => {
        setState(prevState => {
            return {...prevState, showForm: true, rowId: parseInt(id)};
        });
    }
    const handleClickItemViewCondicionEspecial = (id) => {
        const item = items.find(x => x.id === id);
        const dataCondicionEspecial = {
            idCuenta: item.idCuenta,
            numeroPartida: item.numeroPartida,
            codigoDelegacion: item.codigoDelegacion,
            numeroMovimiento: item.numeroMovimiento,
            showForm: true,
            modeEdit: (state.mode === OPERATION_MODE.EDIT)
        };
        setDataCondicionEspecial(dataCondicionEspecial);
    }
    const handleClickViewCondicionEspecial = () => {
        const dataCondicionEspecial = {
            idCuenta: state.idCuenta,
            numeroPartida: 0,
            codigoDelegacion: '',
            numeroMovimiento: 0,
            showForm: true,
            modeEdit: false
        };
        setDataCondicionEspecial(dataCondicionEspecial);
    }
    const handleClickSalir = () => {
        window.close();
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }
    const callbackSuccessFindCuenta = (response) => {
        response.json()
        .then((cuenta) => {
            setCuenta(cuenta);
            setState(prevState => {
                return {...prevState, loading: false, idContribuyente: cuenta.idContribuyentePrincipal};
            });
            FindContribuyente(cuenta.idContribuyentePrincipal);
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    }
    const callbackSuccessFindContribuyente = (response) => {
        response.json()
        .then((contribuyente) => {
            setContribuyente(contribuyente);
            setState(prevState => {
                return {...prevState, loading: false};
            });
            SearchCuentaCorrienteItem();
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    }
    const callbackSuccessSearchCuentaCorrienteItem = (response) => {
        response.json()
        .then((items) => {

            let importeSaldoAnterior = 0;
            items.sort(sortCuentaCorriente);
            for (let index=items.length-1; index>=0; index--) {
                let item = items[index];
                if (item.fechaMovimiento) item.fechaMovimiento = new Date(item.fechaMovimiento);
                if (item.fechaOrigen) item.fechaOrigen = new Date(item.fechaOrigen);
                if (item.fechaVencimiento1) item.fechaVencimiento1 = new Date(item.fechaVencimiento1);
                if (item.fechaVencimiento2) item.fechaVencimiento2 = new Date(item.fechaVencimiento2);
                if (item.fechaVencimiento1Original) item.fechaVencimiento1Original = new Date(item.fechaVencimiento1Original);
                if (item.fechaVencimiento2Original) item.fechaVencimiento2Original = new Date(item.fechaVencimiento2Original);
                if (item.fechaVencimiento1Prorroga) item.fechaVencimiento1Prorroga = new Date(item.fechaVencimiento1Prorroga);
                if (item.fechaVencimiento2Prorroga) item.fechaVencimiento2Prorroga = new Date(item.fechaVencimiento2Prorroga);
                if (item.fechaRegistro) item.fechaRegistro = new Date(item.fechaRegistro);
                if (item.fechaProceso) item.fechaProceso = new Date(item.fechaProceso);
                if (item.fechaCobro) item.fechaCobro = new Date(item.fechaCobro);

                item.codigoTasa = getCodigoTasa(item.idTasa);
                item.descSubTasa = getDescSubTasa(item.idSubTasa);
                item.codigoRubro = !isNull(item.idRubro) ? getCodigoRubro(item.idRubro) : '';

                importeSaldoAnterior = precisionRound(importeSaldoAnterior + item.importeDebe - item.importeHaber);
                item.index = index;
                item.selected = false;
                item.importeSaldo = 0;
                item.importeSaldoFiltro = 0;
                item.importeSaldoAnterior = importeSaldoAnterior;
                item.codigoMovimiento = `${item.codigoDelegacion}-${item.numeroMovimiento}-${item.item}`
                item.operacion = `${item.numeroPartida}-${item.id}`;
            }

            setState(prevState => {
                return {...prevState, loading: false, list: items};
            });
            FilterCuentaCorrienteItem(items);
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        });
    }

    //funciones
    function FindCuenta(idCuenta) {

        setState(prevState => {
        return {...prevState, loading: true};
        });

        const paramsUrl = `/${idCuenta}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CUENTA,
            paramsUrl,
            null,
            callbackSuccessFindCuenta,
            callbackNoSuccess,
            callbackError
        );

    }
    function FindContribuyente(idContribuyente) {

        setState(prevState => {
        return {...prevState, loading: true};
        });

        const paramsUrl = `/${idContribuyente}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CONTRIBUYENTE,
            paramsUrl,
            null,
            callbackSuccessFindContribuyente,
            callbackNoSuccess,
            callbackError
        );

    }
    function SearchCuentaCorrienteItem() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const paramsUrl = (params.filter === 'cuenta') ? `/cuenta/${state.idCuenta}` : `/contribuyente/${state.idContribuyente}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CUENTA_CORRIENTE_ITEM,
            paramsUrl,
            null,
            callbackSuccessSearchCuentaCorrienteItem,
            callbackNoSuccess,
            callbackError
        );

    }
    function FilterCuentaCorrienteItem(fullItems) {
        let items = [...fullItems].filter(f => {

            if (!(formValues.idsTasa.length === 0 || formValues.idsTasa.includes(f.idTasa))) return false;

            if (!(formValues.idsSubTasa.length === 0 || formValues.idsSubTasa.includes(f.idSubTasa))) return false;

            if (!(formValues.numeroPartida === '' || f.numeroPartida.toString() === formValues.numeroPartida)) return false;

            const periodoCuota = `${f.periodo.padStart(4,'0')}-${f.cuota.toString().padStart(2,'0')}`;
            if (!((formValues.descPeriodoCuotaDesde === '0000-00' || periodoCuota.localeCompare(formValues.descPeriodoCuotaDesde) >= 0) &&
                  (formValues.descPeriodoCuotaHasta === '0000-00' || periodoCuota.localeCompare(formValues.descPeriodoCuotaHasta) <= 0))) return false;

            if (!((!formValues.fechaMovimientoDesde || f.fechaMovimiento.getTime() >= formValues.fechaMovimientoDesde.getTime()) &&
                  (!formValues.fechaMovimientoHasta || f.fechaMovimiento.getTime() <= formValues.fechaMovimientoHasta.getTime()))) return false;

            if (!(formValues.idsTipoMovimiento.length === 0 || formValues.idsTipoMovimiento.includes(f.idTipoMovimiento))) return false;

            if (!((formValues.numeroMovimientoDesde === '' || f.numeroMovimiento >= parseInt(formValues.numeroMovimientoDesde)) &&
                  (formValues.numeroMovimientoHasta === '' || f.numeroMovimiento <= parseInt(formValues.numeroMovimientoHasta)))) return false;

            if (!(formValues.movimientoCero || f.importeDebe > 0 || f.importeHaber > 0)) return false;

            return true;
        });

        let importeSaldoFiltro = 0;
        items.sort(sortCuentaCorriente);
        for (let index=items.length-1; index>=0; index--) {
            let item = items[index];
            importeSaldoFiltro = precisionRound(importeSaldoFiltro + item.importeDebe -item.importeHaber);
            item.index = index;
            item.importeSaldoFiltro = importeSaldoFiltro;
            item.importeSaldo = (formValues.saldoAnterior) ? item.importeSaldoAnterior : item.importeSaldoFiltro;
        }

        setItems(items);
    }

    function ApplyFilters() {
        UpdateFilters();
        FilterCuentaCorrienteItem(state.list);
    }
    function UpdateFilters() {
        let labels = [...filters.labels];
        labels.forEach((label) => {
            label.value = formValues[label.field];
        });
        setFilters(prevState => {
            return {...prevState, labels: labels};
        });
    }


    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.showForm &&
            <CuentaCorrienteItemModal
                data={{
                    idCuentaCorrienteItem: state.rowId
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, showForm: false, rowId: 0};
                    });
                }}
            />
        }

        {dataCondicionEspecial.showForm &&
            <CuentaCorrienteCondicionesEspecialesGrid
                data={{
                    idCuenta: dataCondicionEspecial.idCuenta,
                    idTipoTributo: cuenta.idTipoTributo,
                    numeroPartida: dataCondicionEspecial.numeroPartida,
                    codigoDelegacion: dataCondicionEspecial.codigoDelegacion,
                    numeroMovimiento: dataCondicionEspecial.numeroMovimiento
                }}
                onDismiss={() => {
                    setDataCondicionEspecial(prevState => { return {...prevState, showForm: false}; });
                }}
                disabled={!dataCondicionEspecial.modeEdit || !canEditCuentaCorriente}
            />
        }

        <section style={{height:'20px'}}></section>

        <SectionHeading titles={[{ title: 'Tributos' }, { title: 'Cuenta corriente' }]} />

        <section className='section-accordion'>

            <div className='row form-basic mb-3'>
                {cuenta.id > 0 &&
                <div className="col-12 col-md-4 col-lg-2">
                    <label htmlFor="cuenta" className="form-label">Cuenta</label>
                    <InputCuenta
                        name="cuenta"
                        placeholder=""
                        className="form-control"
                        value={cuenta.id}
                        disabled={true}
                    />
                </div>
                }
                <div className="col-12 col-md-8 col-lg-5">
                    <label htmlFor="contribuyente" className="form-label">Contribuyente</label>
                    <InputPersona
                        name="contribuyente"
                        placeholder=""
                        className="form-control"
                        value={contribuyente.idPersona}
                        disabled={true}
                    />
                </div>
            </div>

            <AdvancedSearch
                formValues={formValues}
                formSet={formSet}
                initShowFilters={false}
                labels={filters.labels.filter(f => f.value !== f.valueIgnore)}
                onSearch={ApplyFilters}
            >

                <div className='row form-basic'>

                    <div className="col-12 col-md-6 col-lg-5">
                        <label htmlFor="idsTasa" className="form-label">Tasa</label>
                        <InputEntidadMultiple
                            name="idsTasa"
                            title="Tasa"
                            className="form-control"
                            values={ formValues.idsTasa }
                            onChange={({target}) =>{
                                const idsTasa = target.value ?? [];
                                const idsSubTasa = (idsTasa.length === 0)
                                    ? formValues.idsSubTasa
                                    : formValues.idsSubTasa.filter(id => state.list.some(x => x.idSubTasa === id && idsTasa.includes(x.idTasa)));
                                formSet({...formValues,
                                    idsTasa: idsTasa,
                                    descTasa: getDescSeleccion(idsTasa, getDescTasa),
                                    idsSubTasa: idsSubTasa,
                                    descSubTasa: getDescSeleccion(idsSubTasa, getDescSubTasa)
                                });
                            }}
                            filter={(f) => state.list.some(x => x.idTasa === f.id)}
                            columns={columnsTasa}
                            entidad="Tasa"
                        />
                    </div>
                    <div className="col-12 col-md-6 col-lg-5">
                        <label htmlFor="idsSubTasa" className="form-label">Sub tasa</label>
                        <InputEntidadMultiple
                            name="idsSubTasa"
                            title="Sub tasa"
                            className="form-control"
                            values={ formValues.idsSubTasa }
                            onChange={({target}) =>{
                                const idsSubTasa = target.value ?? [];
                                formSet({...formValues, idsSubTasa: idsSubTasa, descSubTasa: getDescSeleccion(idsSubTasa, getDescSubTasa)});
                            }}
                            filter={(f) => state.list.some(x => x.idSubTasa === f.id && (formValues.idsTasa.length === 0 || formValues.idsTasa.includes(x.idTasa)))}
                            columns={columnsSubTasa}
                            entidad="SubTasa"
                        />
                    </div>
                    <div className="col-6 col-md-6 col-lg-2">

                        <label htmlFor="numeroPartida" className="form-label">N° Partida</label>
                        <InputWithEnterEvent
                            useInputCodigo={true}
                            name="numeroPartida"
                            placeholder=""
                            className="form-control"
                            value={ formValues.numeroPartida }
                            onChange={formHandle}
                            onEnter={ApplyFilters}
                        />
                    </div>

                    <div className="col-6 col-md-4 col-lg-2">
                        <label htmlFor="periodoDesde" className="form-label">Período desde</label>
                        <InputEjercicio
                            name="periodoDesde"
                            placeholder=""
                            className="form-control"
                            value={ formValues.periodoDesde }
                            onChange={({target}) =>{
                                const descPeriodoCuotaDesde = `${target.value.padStart(4,'0')}-${formValues.cuotaDesde.padStart(2,'0')}`;
                                formSet({...formValues, periodoDesde: target.value, descPeriodoCuotaDesde: descPeriodoCuotaDesde});
                            }}
                        />
                    </div>
                    <div className="col-6 col-md-2 col-lg-2">
                        <label htmlFor="cuotaDesde" className="form-label">Cuota</label>
                        <InputWithEnterEvent
                            useInputCodigo={true}
                            maxLength={2}
                            name="cuotaDesde"
                            placeholder=""
                            className="form-control"
                            value={ formValues.cuotaDesde }
                            onChange={({target}) =>{
                                const descPeriodoCuotaDesde = `${formValues.periodoDesde.padStart(4,'0')}-${target.value.padStart(2,'0')}`;
                                formSet({...formValues, cuotaDesde: target.value, descPeriodoCuotaDesde: descPeriodoCuotaDesde});
                            }}
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-6 col-md-4 col-lg-2 ">
                        <label htmlFor="periodoHasta" className="form-label">Período hasta</label>
                        <InputEjercicio
                            name="periodoHasta"
                            placeholder=""
                            className="form-control"
                            value={ formValues.periodoHasta }
                            onChange={({target}) =>{
                                const descPeriodoCuotaHasta = `${target.value.padStart(4,'0')}-${formValues.cuotaHasta.padStart(2,'0')}`;
                                formSet({...formValues, periodoHasta: target.value, descPeriodoCuotaHasta: descPeriodoCuotaHasta});
                            }}
                        />
                    </div>
                    <div className="col-6 col-md-2 col-lg-2">
                        <label htmlFor="cuotaHasta" className="form-label">Cuota</label>
                        <InputWithEnterEvent
                            useInputCodigo={true}
                            maxLength={2}
                            name="cuotaHasta"
                            placeholder=""
                            className="form-control"
                            value={ formValues.cuotaHasta }
                            onChange={({target}) =>{
                                const descPeriodoCuotaHasta = `${formValues.periodoHasta.padStart(4,'0')}-${target.value.padStart(2,'0')}`;
                                formSet({...formValues, cuotaHasta: target.value, descPeriodoCuotaHasta: descPeriodoCuotaHasta});
                            }}
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-6 col-md-6 col-lg-2">
                        <label htmlFor="fechaMovimientoDesde" className="form-label">Fecha desde</label>
                        <DatePickerCustom
                            name="fechaMovimientoDesde"
                            placeholder=""
                            className="form-control"
                            value={ formValues.fechaMovimientoDesde }
                            onChange={({target}) =>{
                                const descFechaMovimientoDesde = getDateToString(target.value, false);
                                formSet({...formValues, fechaMovimientoDesde: target.value, descFechaMovimientoDesde: descFechaMovimientoDesde});
                            }}
                        />
                    </div>
                    <div className="col-6 col-md-6 col-lg-2">
                        <label htmlFor="fechaMovimientoHasta" className="form-label">Fecha hasta</label>
                        <DatePickerCustom
                            name="fechaMovimientoHasta"
                            placeholder=""
                            className="form-control"
                            value={ formValues.fechaMovimientoHasta }
                            onChange={({target}) =>{
                                const descFechaMovimientoHasta = getDateToString(target.value, false);
                                formSet({...formValues, fechaMovimientoHasta: target.value, descFechaMovimientoHasta: descFechaMovimientoHasta});
                            }}
                            minValue={formValues.fechaMovimientoDesde}
                        />
                    </div>
                    <div className="col-6 col-md-6 col-lg-2">
                        <label htmlFor="idsTipoMovimiento" className="form-label">Tipo movimiento</label>
                        <InputEntidadMultiple
                            name="idsTipoMovimiento"
                            title="Tipo movimiento"
                            className="form-control"
                            values={ formValues.idsTipoMovimiento }
                            onChange={({target}) =>{
                                const rows = target.rows ?? [];
                                let descTipoMovimiento = '';
                                if (rows.length === 1) descTipoMovimiento = `${rows[0].codigo} - ${rows[0].nombre}`;
                                else if (rows.length > 1) descTipoMovimiento = `${rows.length} Seleccionados`;
                                formSet({...formValues, idsTipoMovimiento: target.value ?? [], descTipoMovimiento: descTipoMovimiento});
                            }}
                            filter={(f) => state.list.some(x => x.idTipoMovimiento === f.id)}
                            entidad="TipoMovimiento"
                        />
                    </div>

                    <div className="col-6 col-md-6 col-lg-2">
                        <label htmlFor="numeroMovimientoDesde" className="form-label">N° Movimiento desde</label>
                        <InputWithEnterEvent
                            useInputCodigo={true}
                            name="numeroMovimientoDesde"
                            placeholder=""
                            className="form-control"
                            value={ formValues.numeroMovimientoDesde }
                            onChange={formHandle}
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-6 col-md-6 col-lg-2">

                        <label htmlFor="numeroMovimientoHasta" className="form-label">N° Movimiento hasta</label>
                        <InputWithEnterEvent
                            useInputCodigo={true}
                            name="numeroMovimientoHasta"
                            placeholder=""
                            className="form-control"
                            value={ formValues.numeroMovimientoHasta }
                            onChange={formHandle}
                            onEnter={ApplyFilters}
                        />
                    </div>
                    <div className="col-12 col-md-12 col-lg-6">
                        <CheckboxButtonGroup label="Opciones">
                            <CheckboxButton
                                name="saldoAnterior"
                                label="Considerar el saldo anterior"
                                checked={formValues.saldoAnterior}
                                onChange={({target}) =>{
                                    const descSaldoAnterior = (target.checked) ? 'Sí' : 'No';
                                    formSet({...formValues, saldoAnterior: target.checked, descSaldoAnterior: descSaldoAnterior});
                                }}
                            />
                            <CheckboxButton
                                name="movimientoCero"
                                label="Mostrar movimientos en cero"
                                checked={formValues.movimientoCero}
                                onChange={({target}) =>{
                                    const descMovimientoCero = (target.checked) ? 'Sí' : 'No';
                                    formSet({...formValues, movimientoCero: target.checked, descMovimientoCero: descMovimientoCero});
                                }}
                            />
                        </CheckboxButtonGroup>
                    </div>

                </div>

            </AdvancedSearch>

            <div className="m-top-20">

                <TableCustom
                    showFooter={true}
                    className={'TableCustomBase'}
                    columns={activeColumns}
                    data={items}
                    pageSize={50}
                />

            </div>

        </section>

        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                <button className="btn back-button float-start" onClick={ (event) => handleClickSalir() }>Volver</button>
                {state.idCuenta > 0 &&
                <button className="btn btn-outline-primary float-end" onClick={ (event) => handleClickViewCondicionEspecial() }>Condiciones especiales</button>
                }
            </div>
        </footer>

    </>
    )
}

export default CuentaCorrienteView;
